import {tagsApi} from "@dv5/api"
import type {ErrorProcessor} from "@dv5/api"
import {Button} from "@dv5/components"
import {DvTag} from "@dv5/domain"
import {FC, useEffect, useRef, useState} from "react"
import './AssignWidgetsToTagsDialog.less'

interface Props {
    isOpen: boolean
    onClose: () => void
    onAssign: (tagIds: string[]) => void
    additionalId: string
    additionalType: string
}

export const AssignWidgetsToTagsDialog: FC<Props> = ({
    isOpen,
    onClose,
    onAssign,
    additionalId,
    additionalType,
}) => {
    const [tags, setTags] = useState<DvTag[]>([])
    const [selectedTags, setSelectedTags] = useState<string[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const dialogRef = useRef<HTMLDivElement>(null)
    const loadedRef = useRef(false)

    useEffect(() => {
        if (!isOpen) {
            setSelectedTags([])
            loadedRef.current = false
            return
        }
        if (loadedRef.current) return
        loadedRef.current = true
        setIsLoading(true)
        const noop: ErrorProcessor = {displayError: () => {}, displayStep: () => {}}
        tagsApi.loadTags(additionalId, additionalType, noop, (loadedTags) => {
            setTags(loadedTags)
            setIsLoading(false)
        }).catch(console.error)
    }, [isOpen, additionalId, additionalType])

    if (!isOpen) return null

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
            onClose()
        }
    }

    const toggleTag = (tagId: string) => {
        setSelectedTags(prev =>
            prev.includes(tagId)
                ? prev.filter(id => id !== tagId)
                : [...prev, tagId]
        )
    }

    const handleAssign = () => {
        onAssign(selectedTags)
        onClose()
    }

    return (
        <div className="assign-widgets-to-tags-dialog-overlay" onClick={handleOverlayClick}>
            <div className="assign-widgets-to-tags-dialog" ref={dialogRef}>
                <h3>Select Tags</h3>
                {isLoading ? (
                    <div className="assign-widgets-to-tags-loading">Loading tags...</div>
                ) : tags.length === 0 ? (
                    <div className="assign-widgets-to-tags-empty">No tags found</div>
                ) : (
                    <div className="assign-widgets-to-tags-list">
                        {tags.map(tag => (
                            <label
                                key={tag.id}
                                className={[
                                    'assign-widgets-to-tags-item',
                                    selectedTags.includes(tag.id) ? 'selected' : '',
                                    tag.obsoleted ? 'obsoleted' : '',
                                ].filter(Boolean).join(' ')}
                                onMouseDown={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    toggleTag(tag.id)
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedTags.includes(tag.id)}
                                    onChange={() => {}}
                                />
                                <div className="assign-widgets-to-tags-item-info">
                                    <span className="assign-widgets-to-tags-item-title">{tag.title}</span>
                                    <span className="assign-widgets-to-tags-item-type">{tag.typeTitle}</span>
                                </div>
                            </label>
                        ))}
                    </div>
                )}
                <div className="assign-widgets-to-tags-buttons">
                    <Button title="Cancel" onClick={onClose} className="cancel-dialog-button" />
                    <Button title="Assign" onClick={handleAssign} className="assign-dialog-button" />
                </div>
            </div>
        </div>
    )
}
